import { Component, ErrorInfo, ReactNode } from 'react';

import { toAbsoluteUrl } from '../../theme/helpers';

// Guards the one-time auto-reload below so a *persistent* chunk failure
// (e.g. the deploy is actually broken) shows the fallback UI instead of
// reloading forever.
const CHUNK_RELOAD_GUARD_KEY = 'sims-chunk-reload-attempted';

// A stale tab that's been open across a deploy will reference JS/CSS chunk
// hashes that no longer exist on the server once it tries to lazy-load a
// route. Webpack/CRA throws a distinctly-named/worded error for this.
const isChunkLoadError = (error: Error): boolean =>
  error.name === 'ChunkLoadError' ||
  /Loading (chunk|CSS chunk) [\w-]+ failed/i.test(error.message) ||
  /Failed to fetch dynamically imported module/i.test(error.message);

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

// There was previously no ErrorBoundary anywhere in this app, so any error
// thrown during render (a stale-deploy chunk 404, an auth-refresh race,
// anything) unmounted the entire tree -- a blank page with no way back
// short of guessing to hit refresh. This catches that, auto-recovers from
// the stale-chunk case with a single forced reload, and otherwise shows a
// recoverable error screen instead of nothing.
class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  componentDidMount() {
    // Reaching a mount means this page load didn't need the auto-reload
    // below -- clear the guard so a later, unrelated chunk failure (e.g.
    // from a subsequent deploy, much later in the same tab session) still
    // gets its own single auto-reload attempt instead of going straight to
    // the fallback screen.
    sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY);
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Unhandled render error caught by ErrorBoundary:', error, info);

    if (isChunkLoadError(error) && !sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY)) {
      sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, '1');
      window.location.reload();
    }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="d-flex flex-column flex-root">
        <div className="d-flex flex-column flex-column-fluid">
          <div className="d-flex flex-column flex-column-fluid text-center p-10 py-lg-15">
            <div className="pt-lg-10 mb-10">
              <h1 className="fw-bolder fs-2qx text-gray-800 mb-10">Something went wrong</h1>
              <div className="fw-bold fs-3 text-muted mb-15">
                The page ran into a problem and couldn&apos;t continue.
                <br />
                Reloading usually fixes this.
              </div>
              <div className="text-center">
                <button
                  type="button"
                  className="btn btn-lg btn-primary fw-bolder"
                  onClick={() => window.location.reload()}
                >
                  Reload page
                </button>
              </div>
            </div>
            <div
              className="d-flex flex-row-auto bgi-no-repeat bgi-position-x-center bgi-size-contain bgi-position-y-bottom min-h-100px min-h-lg-350px"
              style={{
                backgroundImage: `url('${toAbsoluteUrl('/media/illustrations/sketchy-1/17.png')}')`,
              }}
            />
          </div>
        </div>
      </div>
    );
  }
}

export { ErrorBoundary };
